import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Image as ImageIcon, Loader2, RefreshCw } from "lucide-react";

/**
 * Reusable image uploader with drag/drop + preview + replace + remove.
 * Validates jpg/jpeg/png/webp · max 5MB.
 * Calls POST /api/admin/upload-image (multipart) → returns { url }.
 */
const ALLOWED = ["image/jpeg", "image/png", "image/webp"];
const MAX_MB = 5;

export default function ImageUploader({ value, onChange, label = "Image", aspect = "16/9", testId = "image-uploader" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!ALLOWED.includes(file.type)) { toast.error("Only JPG, PNG or WEBP allowed"); return; }
    if (file.size > MAX_MB * 1024 * 1024) { toast.error(`Max file size ${MAX_MB} MB`); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    setProgress(0);
    try {
      const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (er) {
      toast.error(er.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="text-xs text-white/60">{label}</div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPickFile} className="hidden" data-testid={`${testId}-input`} />

      {value ? (
        <div className="relative group bg-[#0a0a0a] border border-white/10 rounded-xl overflow-hidden" style={{ aspectRatio: aspect }}>
          <img src={value} alt="preview" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
            <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 backdrop-blur text-white text-xs font-semibold rounded-full px-3 py-1.5 border border-white/20 transition" data-testid={`${testId}-replace`}>
              <RefreshCw className="w-3.5 h-3.5" /> Replace
            </button>
            <button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-1.5 bg-red-500/80 hover:bg-red-500 text-white text-xs font-semibold rounded-full px-3 py-1.5 transition" data-testid={`${testId}-remove`}>
              <X className="w-3.5 h-3.5" /> Remove
            </button>
          </div>
          {uploading && (
            <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-2">
              <Loader2 className="w-6 h-6 text-[#FF0066] animate-spin" />
              <div className="text-xs text-white/80">Uploading… {progress}%</div>
              <div className="w-32 h-1 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-[#FF0066]" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div
          onClick={() => !uploading && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`relative cursor-pointer bg-[#0a0a0a] border-2 border-dashed rounded-xl flex flex-col items-center justify-center text-center transition ${dragOver ? "border-[#FF0066] bg-[#FF0066]/5" : "border-white/15 hover:border-[#FF0066]/40"} ${uploading ? "pointer-events-none" : ""}`}
          style={{ aspectRatio: aspect }}
          data-testid={`${testId}-dropzone`}
        >
          {uploading ? (
            <>
              <Loader2 className="w-7 h-7 text-[#FF0066] animate-spin" />
              <div className="text-xs text-white/70 mt-3">Uploading… {progress}%</div>
              <div className="w-40 h-1 bg-white/10 rounded-full overflow-hidden mt-2">
                <div className="h-full bg-[#FF0066]" style={{ width: `${progress}%` }} />
              </div>
            </>
          ) : (
            <>
              <div className="w-10 h-10 rounded-lg bg-[#FF0066]/10 flex items-center justify-center mb-2">
                <Upload className="w-5 h-5 text-[#FF0066]" />
              </div>
              <div className="text-sm font-semibold text-white">Drop image here or click to upload</div>
              <div className="text-[11px] text-white/50 mt-1">JPG, PNG, WEBP · max {MAX_MB} MB</div>
            </>
          )}
        </div>
      )}

      <div className="flex items-center gap-2">
        <input
          type="url"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="…or paste image URL"
          className="flex-1 bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF0066]"
          data-testid={`${testId}-url`}
        />
        {value && <ImageIcon className="w-4 h-4 text-emerald-400" />}
      </div>
    </div>
  );
}
